/*
  Global safe logging shim
  - In production: console.log/info/debug are no-ops
  - console.warn/error are sanitized to avoid leaking sensitive data
  - In development: no changes
*/

type ConsoleMethod = (...args: unknown[]) => void;

const redactEmail = (value: string): string => {
  return value.replace(/\b([A-Z0-9._%+-]+)@([A-Z0-9.-]+\.[A-Z]{2,})\b/gi, (_m, user, domain) => {
    const masked = user.length <= 2 ? '*'.repeat(user.length) : `${user[0]}***${user[user.length - 1]}`;
    return `${masked}@${domain}`;
  });
};

const redactBearer = (value: string): string => {
  return value.replace(/Bearer\s+[A-Za-z0-9-_\.]+/gi, 'Bearer [REDACTED]');
};

const redactJwt = (value: string): string => {
  // Base64url.base64url.base64url
  return value.replace(/[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+/g, '[JWT_REDACTED]');
};

const redactApiKeys = (value: string): string => {
  // Common prefixes (e.g., sbp_ for Supabase management tokens)
  return value
    .replace(/sbp_[A-Za-z0-9]+/g, '[REDACTED]')
    .replace(/sk-[A-Za-z0-9]+/g, '[REDACTED]');
};

const redactLongNumbers = (value: string): string => {
  // Mask sequences of 11+ digits (CPF/CNPJ/IDs)
  return value.replace(/\b\d{11,}\b/g, (m) => `${m.slice(0, 3)}***${m.slice(-2)}`);
};

const sanitizeValue = (val: unknown): unknown => {
  if (typeof val !== 'string') return val;
  let result = val;
  result = redactEmail(result);
  result = redactBearer(result);
  result = redactJwt(result);
  result = redactApiKeys(result);
  result = redactLongNumbers(result);
  return result;
};

const sanitizeArgs = (args: unknown[]): unknown[] => args.map(sanitizeValue);

(() => {
  if (import.meta && import.meta.env && import.meta.env.PROD) {
    const originalWarn: ConsoleMethod = console.warn.bind(console);
    const originalError: ConsoleMethod = console.error.bind(console);

    // Silence verbose logs in production
    console.log = () => {};
    console.info = () => {};
    console.debug = () => {};

    // Keep warnings and errors, but sanitize content
    console.warn = (...args: unknown[]) => originalWarn(...sanitizeArgs(args));
    console.error = (...args: unknown[]) => originalError(...sanitizeArgs(args));
  }
})();


